import { useEffect, useState } from "react";
import { api, type HrPeaksResponse } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { fmtMins } from "./_shared";

const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function HeartPeaks() {
  const [data, setData] = useState<HrPeaksResponse | null | "error">(null);

  useEffect(() => {
    api.heart
      .peaks(7)
      .then(setData)
      .catch(() => setData("error"));
  }, []);

  const d = data && data !== "error" ? data : null;
  const has = d != null && d.periods.length > 0;

  return (
    <div className="animate-fade-in">
      <h2 className="section-title mb-1">Peak activity periods</h2>
      <p className="text-ink-subtle text-small mb-4">
        Stretches over the last 7 days where your heart rate stayed elevated
        {d ? ` (above ~${d.threshold_bpm} bpm)` : ""} for 10 minutes or more — workouts, brisk
        walks, or anything that got you moving.
      </p>

      {!has ? (
        <EmptyState error={data === "error"} title="No peak periods yet">
          When your heart rate stays elevated for a sustained stretch, it'll show up here ranked by
          intensity. Go for a walk or a workout with the watch on.
        </EmptyState>
      ) : (
        <div className="card divide-y divide-ink-line-soft">
          {d!.periods.map((p, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <div className="text-sm text-ink">{dayLabel(p.start)}</div>
                <div className="mt-0.5 text-xs text-ink-subtle">
                  {hhmm(p.start)}–{hhmm(p.end)} · {fmtMins(p.duration_min)} · avg {p.avg_bpm} bpm
                </div>
              </div>
              <div className="ml-3 shrink-0 text-right">
                <div className="font-display text-xl tabular-nums text-accent">{p.peak_bpm}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink-subtle">peak bpm</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
